import { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Phone } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Shared";
import { API, PHONE_DISPLAY, PHONE_TEL, EMAIL } from "@/data/content";
import { useSEO } from "@/hooks/useSEO";

const PROJECT_TYPES = ["Kitchen Renovation", "Bathroom Renovation", "Basement Renovation", "Flooring", "Interior Renovation & Painting", "Complete Home Renovation", "Other"];
const TIMELINES = ["Urgent — as soon as possible", "Within 2–4 weeks", "Within 1–2 months", "Within 2–3 months", "3+ months (just planning)", "Custom date"];
const BUDGETS = ["Under $10,000", "$10,000 – $25,000", "$25,000 – $50,000", "$50,000 – $100,000", "$100,000+", "Not sure yet — need guidance"];

const inputCls = "w-full bg-bone border border-obsidian/20 px-4 py-3 text-sm text-obsidian placeholder:text-stonemuted focus:border-teak transition-colors";
const labelCls = "block text-xs font-semibold uppercase tracking-wider text-stonemuted mb-2";

export default function Estimate() {
  useSEO(
    "Book a Free Estimate | MTC Home Renovation GTA",
    "Book a free, fixed-price renovation estimate from MTC Home Renovation. Free in-home consultation across the GTA & Kitchener-Waterloo. Call (519) 760-0444."
  );
  const location = useLocation();
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    project_type: location.state?.projectType || "Kitchen Renovation",
    property_info: "", project_size: location.state?.size || "",
    budget: "", timeline: "", custom_date: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const timeline = form.timeline === "Custom date" && form.custom_date
      ? `Preferred start: ${form.custom_date}`
      : form.timeline;
    try {
      await axios.post(`${API}/estimate`, {
        name: form.name, email: form.email, phone: form.phone,
        project_type: form.project_type, property_info: form.property_info,
        project_size: form.project_size, budget: form.budget,
        timeline, message: form.message,
      });
      setDone(true);
      toast.success("Estimate request received — we'll be in touch within 24 hours.");
    } catch {
      toast.error("Something went wrong. Please call us directly at " + PHONE_DISPLAY);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 sm:py-24" data-testid="estimate-page">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>Book a Free Estimate</Eyebrow>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-medium tracking-tight leading-[1.1] text-obsidian">
              Tell us about your <em className="italic text-teak">project.</em>
            </h1>
            <p className="mt-6 text-stone-600 leading-relaxed">
              Send the form and we'll call within 24 hours to book your free in-home consultation and exact fixed-price quote.
            </p>
            <div className="mt-8 border border-obsidian/15 bg-parchment/60 p-6">
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-stonemuted">Prefer to talk?</p>
              <a href={PHONE_TEL} data-testid="estimate-call-banner" className="mt-3 flex items-center gap-3 group">
                <span className="w-11 h-11 bg-obsidian text-bone flex items-center justify-center group-hover:bg-teak transition-colors duration-300">
                  <Phone size={18} />
                </span>
                <span>
                  <span className="block font-display text-2xl font-semibold text-teak">{PHONE_DISPLAY}</span>
                  <span className="block text-xs text-stonemuted">Mon – Sat, 7:30 AM – 6:30 PM</span>
                </span>
              </a>
            </div>
            <p className="mt-6 text-sm text-stone-600 leading-relaxed">
              Photos help us quote faster — email them to{" "}
              <a href={`mailto:${EMAIL}`} data-testid="estimate-email-link" className="text-teak hover:text-teakdark font-medium">{EMAIL}</a>{" "}
              after submitting.
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            {done ? (
              <div className="border border-obsidian/15 bg-white p-10 sm:p-14 text-center" data-testid="estimate-success">
                <CheckCircle2 size={48} className="mx-auto text-teak" />
                <h2 className="mt-6 font-display text-3xl font-medium text-obsidian">Request received.</h2>
                <p className="mt-4 text-stone-600 leading-relaxed max-w-md mx-auto">
                  Thank you, {form.name.split(" ")[0]}. We'll call <strong>{form.phone}</strong> within 24 hours to book your free in-home estimate.
                </p>
                <a href={PHONE_TEL} data-testid="estimate-success-call" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-teak hover:text-teakdark">
                  <Phone size={15} /> Need it faster? Call {PHONE_DISPLAY}
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-obsidian/15 bg-white p-7 sm:p-10" data-testid="estimate-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls} htmlFor="est-name">Full name *</label>
                    <input id="est-name" data-testid="estimate-name-input" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Smith" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="est-phone">Phone *</label>
                    <input id="est-phone" data-testid="estimate-phone-input" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(416) 555-0123" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="est-email">Email *</label>
                    <input id="est-email" data-testid="estimate-email-input" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@email.com" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="est-type">Project type *</label>
                    <select id="est-type" data-testid="estimate-type-select" className={inputCls} value={form.project_type} onChange={set("project_type")}>
                      {PROJECT_TYPES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="est-property">Property / home info</label>
                    <input id="est-property" data-testid="estimate-property-input" className={inputCls} value={form.property_info} onChange={set("property_info")} placeholder="e.g. 2-storey detached in Vaughan" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="est-size">Approx. project size (sq ft)</label>
                    <input id="est-size" data-testid="estimate-size-input" type="number" min="0" className={inputCls} value={form.project_size} onChange={set("project_size")} placeholder="e.g. 400" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="est-budget">Budget range *</label>
                    <select id="est-budget" data-testid="estimate-budget-select" required className={inputCls} value={form.budget} onChange={set("budget")}>
                      <option value="" disabled>Select your budget range</option>
                      {BUDGETS.map((b) => <option key={b}>{b}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="est-timeline">Desired timeline *</label>
                    <select id="est-timeline" data-testid="estimate-timeline-select" required className={inputCls} value={form.timeline} onChange={set("timeline")}>
                      <option value="" disabled>When do you want to start?</option>
                      {TIMELINES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                    {form.timeline === "Custom date" && (
                      <input
                        type="date"
                        data-testid="estimate-custom-date-input"
                        className={`${inputCls} mt-3`}
                        value={form.custom_date}
                        onChange={set("custom_date")}
                      />
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="est-msg">Additional details</label>
                    <textarea id="est-msg" data-testid="estimate-message-input" rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder="What you'd like changed, rooms involved, anything we should know..." />
                  </div>
                </div>
                <button
                  type="submit"
                  data-testid="estimate-submit-button"
                  disabled={loading}
                  className="mt-8 w-full bg-teak text-bone py-4 text-sm font-semibold tracking-wide shadow-lg shadow-teak/25 hover:bg-teakdark transition-colors duration-300 disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Book"}
                </button>
                <p className="mt-4 text-xs text-stonemuted text-center">
                  Your timeline is a preference — the final schedule depends on project scope and availability.
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
